import { memo, forwardRef, Ref } from 'react';
import { Skeleton, Typography, SkeletonProps } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface SkeletonBoxProps extends SkeletonProps {
  text?: string;
  sx?: SxProps<Theme>;
}

const SkeletonBox = memo(
  forwardRef(function SkeletonBox(
    { text, sx, ...props }: SkeletonBoxProps,
    ref: Ref<HTMLDivElement>,
  ) {
    const theme = useTheme();
    const { t } = useTranslation();

    // Translation string
    const strLoading = t('blox.general.loading', { defaultValue: 'Loading...' });
    const displayText = typeof text === 'string' ? text : strLoading;

    return (
      <Skeleton
        id="skeleton-box-loader"
        data-testid="skeleton-box-loader"
        className="skeleton-box-loader"
        component="div"
        variant="rectangular"
        animation="pulse"
        width={props?.width || '100%'}
        height={props?.height || 50}
        sx={{
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(typeof sx === 'function' ? sx(theme) : sx),
        }}
        {...props}
        ref={ref}
      >
        <Typography
          id="skeleton-box-text"
          className="skeleton-box-text"
          data-testid="skeleton-box-text"
          variant="caption"
          sx={{
            p: 1,
            color:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.5)'
                : 'rgba(0, 0, 0, 0.5)',
            visibility: 'visible',
            alignSelf: 'center',
            alignItems: 'center',
          }}
        >
          {displayText}
        </Typography>
      </Skeleton>
    );
  }),
);

export default SkeletonBox;
